import React from 'react'
import { useNavigate } from 'react-router-dom'
import Tooltip from '@mui/material/Tooltip'
import {
    changeDateFormat,
    convertNumberToUSFormat,
    setBreadCrumbRoutes
} from '../../helpers/Util'
import { addMixpanelTrackingEvent } from '../../services/CommonService'

const STATUS_CONFIG = {
    RUNNING: { label: 'Running', className: 'order-status-badge running' },
    PENDING: { label: 'Pending', className: 'order-status-badge pending' },
    COMPLETED: {
        label: 'Completed',
        className: 'order-status-badge completed'
    },
    CANCELLED: { label: 'Cancelled', className: 'order-status-badge cancelled' }
}

const getItemStatus = (item, activeTab) => {
    if (activeTab && activeTab !== 'ALL') return activeTab
    // Infer from item fields when "All" tab is active
    const s = item?.status?.toUpperCase()
    if (!s) return 'RUNNING'
    if (s === 'COMPLETED' || s === 'ON_SHIPMENT') return 'COMPLETED'
    if (s === 'RUNNING') return 'RUNNING'
    if (s === 'PENDING' || s === 'INITIALIZED') {
        if (item?.approvalStatus === 'CANCELLED') return 'CANCELLED'
        return 'PENDING'
    }
    return s
}

const renderETD = (item) => {
    const firstDate = item?.deliveryDateList?.[0]
    if (!firstDate) return '—'
    const formatted = changeDateFormat(firstDate, 'YYYY-MM-DD', 'DD MMM, YYYY')
    if (item?.timeLeft == null) return formatted
    const daysText =
        Math.sign(item.timeLeft) === -1
            ? `${Math.abs(item.timeLeft)}d overdue`
            : `in ${item.timeLeft}d`
    return `${formatted} ${daysText}`
}

const OrderTableView = ({ orderStore, activeTab }) => {
    const navigate = useNavigate()
    const orders = orderStore?.orderResponse?.data ?? []

    const handleRoute = (item) => {
        const newRoute = `/orders/view/${item.orderId}`
        const visitingRoutes = [
            {
                key: window.location.pathname + window.location.search || '',
                value: 'My Dashboard'
            },
            {
                key: window.location.pathname + window.location.search || '',
                value: 'Order'
            },
            {
                key: newRoute,
                value: item?.orderRefNumber
            }
        ]
        setBreadCrumbRoutes(visitingRoutes)
        addMixpanelTrackingEvent({}, 'BUYER_ORDER_ON_CLICK')
        navigate(newRoute)
    }

    if (!orders.length) return null

    return (
        <div className='order-table-view mt-[14px]'>
            <div className='overflow-x-auto'>
                <table className='table order-list-table w-full'>
                    <thead>
                        <tr>
                            <th>PO</th>
                            <th>Style</th>
                            <th className='text-right'>Qty</th>
                            <th className='text-right'>Value</th>
                            <th>1st ETD</th>
                            <th>Progress</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orders.map((item, index) => {
                            const status = getItemStatus(item, activeTab)
                            const statusCfg =
                                STATUS_CONFIG[status] ||
                                STATUS_CONFIG['RUNNING']
                            const poText = item?.poNumberList?.join(', ') || '—'
                            const styleName =
                                item?.name ||
                                (item?.orderProductList?.[0]?.name ?? '—')
                            const etdText = renderETD(item)
                            const progress = item?.percentageOfCompleteness ?? 0

                            return (
                                <tr
                                    key={`order_table_${index}`}
                                    className='cursor-pointer hover:bg-gray-50'
                                    onClick={() => handleRoute(item)}
                                >
                                    <td>
                                        <Tooltip
                                            title={poText}
                                            placement='top'
                                            arrow
                                        >
                                            <span className='regular-14 weight-500 black'>
                                                {poText.length > 20
                                                    ? `${poText.slice(0, 20)}…`
                                                    : poText}
                                            </span>
                                        </Tooltip>
                                    </td>
                                    <td>
                                        <Tooltip
                                            title={styleName}
                                            placement='top'
                                            arrow
                                        >
                                            <span className='regular-14 black'>
                                                {styleName.length > 30
                                                    ? `${styleName.slice(
                                                          0,
                                                          30
                                                      )}…`
                                                    : styleName}
                                            </span>
                                        </Tooltip>
                                    </td>
                                    <td className='text-right'>
                                        <span className='regular-14 black'>
                                            {item?.orderQuantity != null
                                                ? item.orderQuantity.toLocaleString()
                                                : '—'}
                                        </span>
                                    </td>
                                    <td className='text-right'>
                                        <span className='regular-14 weight-500 black'>
                                            {item?.orderValue != null
                                                ? `$${convertNumberToUSFormat(
                                                      item.orderValue
                                                  )}`
                                                : '—'}
                                        </span>
                                    </td>
                                    <td>
                                        <span className='regular-14 gray_300'>
                                            {etdText}
                                        </span>
                                    </td>
                                    <td>
                                        {status !== 'PENDING' &&
                                        status !== 'CANCELLED' ? (
                                            <div className='order-table-progress'>
                                                <div className='progress-bar-track'>
                                                    <div
                                                        className='progress-bar-fill'
                                                        style={{
                                                            width: `${progress}%`
                                                        }}
                                                    />
                                                </div>
                                                <span className='regular-12 gray_300 ml-2'>
                                                    {progress}%
                                                </span>
                                            </div>
                                        ) : (
                                            <span className='regular-14 gray_200'>
                                                —
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        <span className={statusCfg.className}>
                                            {statusCfg.label}
                                        </span>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default OrderTableView
